/** @format */

import {Widget} from 'components/PropertyWidget/model';
import {safeDivide} from 'helpers/numberHelpers';

import {WidgetsList} from '../models/widgets';
import {ComplexValue} from '../models/common';

// Цветовая схема для виджета или графика
// Формат записи: oklch(<OKLCH_LIGHTNESS> <OKLCH_CHROMA> 123.45)
// Example: oklch(0.7463,0.1268,0)

export const OKLCH_DEFAULT_COLOR = {
  mode: 'oklch',
  l: 0.7463,
  c: 0.1268,
  h: 0,
} as const;

const WIDGETS_LIST_DATA: WidgetsList = {
  budget: {
    type: 'TypeA',
    label: 'Бюджет',
    token: '₽',
    isPositiveRed: true,
    needSpaceBeforeToken: true,
    roundNumberBy: 0,
    isTooltipDelta: true,
    order: 150,
    showSumDayButton: true,
    chartOrder: 10,
    minChartHeight: 128,
    addForecast: true,
  },
  cpl: {
    type: 'TypeB',
    label: 'CPL',
    token: '₽',
    isPositiveRed: true,
    needSpaceBeforeToken: true,
    roundNumberBy: 0,
    isTooltipDelta: true,
    order: 140,
    showScaleButton: true,
    addForecast: true,
    complexRatioPredicate: value => {
      return safeDivide(value.spent, value.conversions);
    },
  },
  drr: {
    type: 'TypeC',
    label: 'ДРР',
    token: '%',
    isPositiveRed: true,
    roundNumberBy: 1,
    isTooltipDelta: true,
    order: 130,
    showScaleButton: true,
    addForecast: true,
    complexRatioPredicate: value => {
      return safeDivide(value.spent, value.revenue) * 100;
    },
  },
  romi: {
    type: 'TypeC',
    label: 'ROMI',
    token: '%',
    roundNumberBy: 1,
    isTooltipDelta: true,
    order: 120,
    isPositiveRed: false,
    showScaleButton: true,
    addForecast: true,
    complexRatioPredicate: value => {
      return safeDivide(value.revenue, value.spent) * 100;
    },
  },
  income: {
    type: 'TypeD',
    label: 'Доход',
    token: '₽',
    needSpaceBeforeToken: true,
    roundNumberBy: 0,
    isTooltipDelta: true,
    showSumDayButton: true,
    order: 110,
    isPositiveRed: false,
    addForecast: true,
  },
  clicks: {
    type: 'TypeD',
    label: 'Клики',
    roundNumberBy: 0,
    showSumDayButton: true,
    order: 100,
    isTooltipDelta: true,
    isPositiveRed: false,
    addForecast: true,
  },
  cpc: {
    type: 'TypeB',
    label: 'CPC',
    token: '₽',
    isPositiveRed: true,
    needSpaceBeforeToken: true,
    roundNumberBy: 0,
    order: 90,
    isTooltipDelta: true,
    showScaleButton: true,
    addForecast: true,
    complexRatioPredicate: value => {
      return safeDivide(value.spent, value.clicks);
    },
  },
  shows: {
    type: 'TypeD',
    label: 'Показы',
    roundNumberBy: 0,
    isTooltipDelta: true,
    showSumDayButton: true,
    order: 80,
    isPositiveRed: false,
    addForecast: true,
  },
  cpm: {
    type: 'TypeB',
    label: 'CPM',
    token: '₽',
    isPositiveRed: true,
    needSpaceBeforeToken: true,
    roundNumberBy: 0,
    order: 70,
    isTooltipDelta: true,
    showScaleButton: true,
    addForecast: true,
    complexRatioPredicate: value => {
      return safeDivide(value.spent, value.impressions) * 1000;
    },
  },
  avgVideoCompleteCost: {
    type: 'TypeB',
    label: 'Ср. цена досмотра 100% ролика',
    token: '₽',
    isPositiveRed: true,
    needSpaceBeforeToken: true,
    roundNumberBy: 0,
    order: 60,
    isTooltipDelta: true,
    showScaleButton: true,
    addForecast: true,
    complexRatioPredicate: value => {
      return safeDivide(value.spent, value.videoComplete);
    },
  },
  reach: {
    type: 'TypeD',
    label: 'Охват',
    roundNumberBy: 0,
    order: 50,
    isTooltipDelta: true,
    showSumDayButton: true,
    isPositiveRed: false,
    addForecast: true,
  },
  er: {
    type: 'TypeC',
    label: 'ER',
    token: '%',
    roundNumberBy: 1,
    order: 40,
    isPositiveRed: false,
    showScaleButton: true,
    isTooltipDelta: true,
    addForecast: true,
    complexRatioPredicate: value => {
      return safeDivide(value.engagement, value.reach) * 100;
    },
  },
  vtr: {
    type: 'TypeC',
    label: 'VTR',
    token: '%',
    roundNumberBy: 1,
    order: 30,
    isPositiveRed: false,
    showScaleButton: true,
    isTooltipDelta: true,
    addForecast: true,
    complexRatioPredicate: value => {
      return safeDivide(value.videoComplete, value.impressions) * 100;
    },
  },
  ctr: {
    type: 'TypeC',
    label: 'CTR',
    token: '%',
    roundNumberBy: 1,
    order: 20,
    isPositiveRed: false,
    showScaleButton: true,
    isTooltipDelta: true,
    addForecast: true,
    complexRatioPredicate: value => {
      return safeDivide(value.clicks, value.impressions) * 100;
    },
  },
  declines: {
    type: 'TypeC',
    label: 'Отказы',
    token: '%',
    isPositiveRed: true,
    roundNumberBy: 0,
    order: 10,
    showScaleButton: true,
    isTooltipDelta: true,
    addForecast: true,
    complexRatioPredicate: value => {
      return safeDivide(value.bounces, value.sessions) * 100;
    },
  },
  sensorGray: {
    type: 'sensor',
    label: 'Некритичные',
    roundNumberBy: 0,
    order: 5,
    chartOrder: 5,
    isPositiveRed: true,
    isTooltipDelta: true,
  },
  sensorYellow: {
    type: 'sensor',
    label: 'Обычные',
    roundNumberBy: 0,
    order: 6,
    chartOrder: 4,
    isPositiveRed: true,
    isTooltipDelta: true,
  },
  sensorRed: {
    type: 'sensor',
    label: 'Критичные',
    roundNumberBy: 0,
    order: 7,
    chartOrder: 3,
    isPositiveRed: true,
    isTooltipDelta: true,
  },
  conversions: {
    type: 'TypeD',
    label: 'Лиды',
    roundNumberBy: 0,
    order: 85,
    showSumDayButton: true,
    isTooltipDelta: true,
    addForecast: true,
  },

  // Скаттеры

  logs: {
    type: 'scatter',
    label: 'Изменения Яндекс Директ',
    roundNumberBy: 0,
    order: 145,
    link: 'home/logs',
    skipTooltip: true,
    skipValueLabels: true,
  },

  notes: {
    type: 'scatter',
    label: 'Заметки',
    roundNumberBy: 0,
    order: 155,
    skipTooltip: true,
    skipValueLabels: true,
  },

  // Вспомогательные графики для составления производных прогнозов

  videoComplete: {
    type: 'TypeA',
    label: 'videoComplete',
    roundNumberBy: 0,
    addForecast: true,
    hide: true,
  },
  engagement: {
    type: 'TypeA',
    label: 'engagement',
    roundNumberBy: 0,
    addForecast: true,
    hide: true,
  },
  bounces: {
    type: 'TypeA',
    label: 'bounces',
    roundNumberBy: 0,
    addForecast: true,
    hide: true,
  },
  sessions: {
    type: 'TypeA',
    label: 'sessions',
    roundNumberBy: 0,
    addForecast: true,
    hide: true,
  },

  // Слои-заглушки (нереализованные данные)

  stub_weather: {
    type: 'TypeStub',
    label: 'Погода',
    roundNumberBy: 0,
    order: -10,
  },
  stub_seasonality: {
    type: 'TypeStub',
    label: 'Сезонность',
    roundNumberBy: 0,
    order: -20,
  },
  stub_price_index: {
    type: 'TypeStub',
    label: 'Прайс-индекс',
    roundNumberBy: 0,
    order: -30,
  },
  stub_cb_rate: {
    type: 'TypeStub',
    label: 'Ставка ЦБ',
    roundNumberBy: 0,
    order: -40,
  },
  stub_currency: {
    type: 'TypeStub',
    label: 'Курсы валют',
    roundNumberBy: 0,
    order: -50,
  },
};

const WIDGETS_LIST_PROXY_HANDLER: ProxyHandler<Record<string, Widget<ComplexValue>>> = {
  get(target, prop, receiver) {
    if (typeof prop === 'symbol') {
      return Reflect.get(target, prop, receiver);
    }

    const key = String(prop);

    if (key in target) {
      return Reflect.get(target, key, receiver);
    }

    const isFunnelCr = key.startsWith('funnel_cr_');

    const defaultWidget: Widget<ComplexValue> = {
      type: 'TypeA',
      label: isFunnelCr ? 'CR' : (prop as string),
      token: isFunnelCr ? '%' : undefined,
      roundNumberBy: isFunnelCr ? 1 : 0,
      hide: false,
      showSumDayButton: !isFunnelCr,
    };

    return defaultWidget;
  },
};

export const WIDGETS_LIST = new Proxy(WIDGETS_LIST_DATA, WIDGETS_LIST_PROXY_HANDLER);
